/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Community Wellness-Status family (W.2,
 *  operator-cop-doctrine §3A.7 / §8.4 colour-context discipline; §3B.8 the
 *  community AMBIENT register).
 *
 *  This module is the SINGLE source of the community wellness-check status
 *  palette. It is consumed ONLY by the community family:
 *    - CommunitiesLayer map markers (the house glyph fill)
 *    - the CommunityBody side-panel status badge
 *    - the HeadcountEditorForm / WellnessCheckEditorForm status chips
 *
 *  Palette (locked law — do NOT re-tune the hex values here):
 *    planned        grey   #9ca3af
 *    in_progress    amber  #f59e0b
 *    complete       green  #10b981
 *    gap_identified rust   #c2410c   ← Colour Law 5: burnt-rust, NOT a saturated
 *                                       red (red is reserved for urgent anomalies)
 *    (no check)     slate  #64748b   ← neutral ambient, distinct from planned
 *
 *  Overlap debt this lock acknowledges (§3A.7): amber (#f59e0b) ALSO means IMS
 *  Response Level 3 (styles/responseLevel) and OC-Monitored fire. gap_identified's
 *  burnt-rust (#c2410c) is deliberately kept off the saturated-red band the urgent
 *  anomaly owns (Colour Law 5). Those hues mean different things in OTHER families
 *  and must never leak across the boundary — so this module owns its own hexes
 *  and never imports a sibling palette. Colour is never load-bearing alone:
 *  every render of a status colour is paired with the status text label.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards (enforced
 *     by the no-restricted-imports lint rule in eslint.config.js). Importing the
 *     WellnessCheckStatus *type* from @tsr/shared-types is fine (not a palette).
 * ──────────────────────────────────────────────────────────────────────────
 */
import type { WellnessCheckStatus } from '@tsr/shared-types';

/** The four wellness-check status hexes — the single source of truth. */
export const COMMUNITY_STATUS_COLORS: Record<WellnessCheckStatus, string> = {
  planned: '#9ca3af', // grey
  in_progress: '#f59e0b', // amber
  complete: '#10b981', // green
  gap_identified: '#c2410c', // burnt-rust (Colour Law 5 — red is reserved)
};

/** Neutral ambient hue for a community with NO wellness check yet (§3B.8 — the
 *  community sits in the ambient register until it's actively being worked).
 *  Deliberately a muted slate, distinct from `planned`'s lighter grey. */
export const COMMUNITY_STATUS_NONE_COLOR = '#64748b';

/** Short human label, paired with the colour so the hue is never alone. */
export const COMMUNITY_STATUS_LABEL: Record<WellnessCheckStatus, string> = {
  planned: 'Planned',
  in_progress: 'In progress',
  complete: 'Complete',
  gap_identified: 'Gap identified',
};

export interface CommunityStatusStyle {
  /** The status, or 'none' when the community has no wellness check yet. */
  status: WellnessCheckStatus | 'none';
  /** CSS hex for the marker fill + badge. */
  color: string;
  /** Short label — colour is never load-bearing alone. */
  label: string;
}

const NONE_STYLE: CommunityStatusStyle = {
  status: 'none',
  color: COMMUNITY_STATUS_NONE_COLOR,
  label: 'No wellness check',
};

/** True if `v` is one of the four canonical wellness-check statuses. */
export function isWellnessStatus(v: unknown): v is WellnessCheckStatus {
  return (
    v === 'planned' || v === 'in_progress' || v === 'complete' || v === 'gap_identified'
  );
}

/**
 * Resolve the render style for a community's latest wellness-check status.
 * Tolerant of null / unknown (→ the neutral ambient 'none' style, never dropped).
 */
export function communityStatusStyle(
  status: WellnessCheckStatus | null | undefined,
): CommunityStatusStyle {
  if (!isWellnessStatus(status)) return NONE_STYLE;
  return {
    status,
    color: COMMUNITY_STATUS_COLORS[status],
    label: COMMUNITY_STATUS_LABEL[status],
  };
}
